import { useState } from 'react';
import { X, Sparkles, FileText, Eye, Send, ChevronDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import toast from 'react-hot-toast';
import { generateBlog } from '../services/blogGenerator';

const POST_TYPES = [
  { value: 'playbook',   label: 'Playbook', description: 'Repeatable step-by-step operational process' },
  { value: 'concept',    label: 'Concept Deep-Dive', description: 'Agents, RAG, orchestration, LLM ops' },
  { value: 'case-study', label: 'Case Study', description: 'Real project outcome, what broke, what worked' },
  { value: 'comparison', label: 'Comparison', description: 'Tool or approach comparison with a recommendation' },
  { value: 'how-to',     label: 'How-To Guide', description: 'Step-by-step build guide, runnable end to end' },
];

const TOPIC_SUGGESTIONS = {
  'playbook':    'A Repeatable Playbook for Shipping AI Automation Pipelines in 2026',
  'concept':     'Multi-Agent Orchestration Explained: Patterns That Actually Ship',
  'case-study':  "How I Cut a Client's Manual Reporting Workflow From 6 Hours to 6 Minutes",
  'comparison':  'No-Code Automation vs. a Custom Python Pipeline: Which Wins in 2026?',
  'how-to':      'How to Build a Bilingual Content-Loop Engine on Firebase + Gemini',
};

export default function BlogGenerator({ onClose, onPublished }) {
  const [topic, setTopic]           = useState('');
  const [postType, setPostType]     = useState('concept');
  const [keywords, setKeywords]     = useState('');
  const [publishNow, setPublishNow] = useState(false);
  const [loading, setLoading]       = useState(false);
  const [result, setResult]         = useState(null); // { id, title, slug, content }
  const [tab, setTab]               = useState('form'); // 'form' | 'preview'

  const handleTypeSelect = (type) => {
    setPostType(type);
    if (!topic) setTopic(TOPIC_SUGGESTIONS[type] || '');
  };

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error('Enter a topic before generating.');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const data = await generateBlog({ topic, postType, keywords, published: publishNow });
      setResult(data);
      setTab('preview');
      toast.success(`Blog "${data.title}" saved${publishNow ? ' and published' : ' as draft'}!`);
      if (onPublished) onPublished(data);
    } catch (err) {
      toast.error(err.message || 'Generation failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-[24px] w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#667eea] to-[#764ba2]">
          <div className="flex items-center gap-3">
            <Sparkles className="text-white" size={22} />
            <span className="text-white font-semibold text-lg">AI Blog Generator</span>
            <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">AI & Automation</span>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
            <X size={22} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100">
          <button
            onClick={() => setTab('form')}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
              tab === 'form'
                ? 'border-b-2 border-[#667eea] text-[#667eea]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText size={16} /> Configure
          </button>
          <button
            onClick={() => setTab('preview')}
            disabled={!result}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
              tab === 'preview'
                ? 'border-b-2 border-[#667eea] text-[#667eea]'
                : 'text-gray-400 hover:text-gray-600'
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <Eye size={16} /> Preview {result && <span className="bg-green-100 text-green-700 text-xs px-1.5 py-0.5 rounded-full">Ready</span>}
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">

          {/* ── CONFIGURE TAB ── */}
          {tab === 'form' && (
            <div className="p-6 space-y-6">

              {/* Post Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Post Type</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {POST_TYPES.map((pt) => (
                    <button
                      key={pt.value}
                      onClick={() => handleTypeSelect(pt.value)}
                      className={`text-left p-3 rounded-[12px] border-2 transition-all ${
                        postType === pt.value
                          ? 'border-[#667eea] bg-[#f0f4ff]'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <span className={`block text-sm font-medium ${postType === pt.value ? 'text-[#667eea]' : 'text-gray-800'}`}>
                        {pt.label}
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5">{pt.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Topic */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Topic / Title Idea <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g. Multi-Agent Orchestration Explained: Patterns That Actually Ship"
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-200 rounded-[12px] text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#667eea] resize-none"
                />
              </div>

              {/* Keywords */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Extra SEO Keywords <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder="e.g. multi-agent systems, workflow automation, LLM orchestration"
                  className="w-full px-4 py-3 border border-gray-200 rounded-[12px] text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#667eea]"
                />
              </div>

              {/* Publish toggle */}
              <div className="flex items-center gap-3 bg-gray-50 rounded-[12px] p-4">
                <button
                  onClick={() => setPublishNow(!publishNow)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${publishNow ? 'bg-[#667eea]' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${publishNow ? 'translate-x-5' : ''}`} />
                </button>
                <div>
                  <span className="text-sm font-medium text-gray-800">
                    {publishNow ? 'Publish immediately' : 'Save as draft'}
                  </span>
                  <p className="text-xs text-gray-500">
                    {publishNow
                      ? 'Post will be visible on the blog right away.'
                      : 'Post will be hidden until you publish it manually.'}
                  </p>
                </div>
              </div>

              {/* CTA note */}
              <div className="bg-amber-50 border border-amber-200 rounded-[12px] p-3 text-xs text-amber-800">
                <strong>Conversion goal:</strong> The AI will close the post with a single
                consulting/freelance CTA linking to the{' '}
                <a href="/work-with-me" target="_blank" rel="noreferrer" className="underline">Work with us</a>{' '}
                page (UTM-tagged per post) — no course or SaaS pitch.
              </div>
            </div>
          )}

          {/* ── PREVIEW TAB ── */}
          {tab === 'preview' && result && (
            <div className="p-6">
              <div className="bg-green-50 border border-green-200 rounded-[12px] p-4 mb-6 flex items-start gap-3">
                <Send size={18} className="text-green-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-green-800">{result.title}</p>
                  <p className="text-xs text-green-700 mt-0.5">
                    Saved to Firestore · ID: <code className="bg-green-100 px-1 rounded">{result.id}</code>
                    {' · '}{result.published ? <span className="text-green-700 font-medium">Published</span> : <span className="text-amber-700 font-medium">Draft</span>}
                  </p>
                </div>
              </div>

              {/* Rendered markdown preview */}
              <div className="prose prose-sm max-w-none text-gray-800">
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {result.content || '*No preview available.*'}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            {result ? 'Close' : 'Cancel'}
          </button>

          {tab === 'form' && (
            <button
              onClick={handleGenerate}
              disabled={loading || !topic.trim()}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white text-sm font-semibold rounded-[10px] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  Generate Blog
                </>
              )}
            </button>
          )}

          {tab === 'preview' && result && (
            <a
              href={`/blogs/${result.id}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white text-sm font-semibold rounded-[10px] hover:opacity-90 transition-opacity"
            >
              <Eye size={16} /> View Post
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
